import { useEffect, useMemo, useState } from 'react';
import { UAParser } from 'ua-parser-js';

export type UAInfo = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  os: string;
  browser: string;
  browserVersion: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  isStandalonePWA: boolean;
};

const MOBILE_MAX_WIDTH = 767;
const TABLET_MAX_WIDTH = 1023;

function deviceTypeFromWidth(windowInnerWidth: number): 'mobile' | 'tablet' | 'desktop' {
  if (windowInnerWidth <= MOBILE_MAX_WIDTH) return 'mobile';
  if (windowInnerWidth <= TABLET_MAX_WIDTH) return 'tablet';
  return 'desktop';
}

export function computeUserAgentInfo(
  userAgentParserResult: ReturnType<UAParser['getResult']>,
  windowInnerWidth: number,
  isStandalonePWA: boolean
): UAInfo {
  const operatingSystem = userAgentParserResult.os?.name ?? '';
  const webBrowser = userAgentParserResult.browser?.name ?? '';
  const webBrowserVersion = userAgentParserResult.browser?.version ?? '';

  const parserDeviceType =
    (userAgentParserResult.device?.type as 'mobile' | 'tablet' | undefined) ?? undefined;

  const deviceTypeByWidth = deviceTypeFromWidth(windowInnerWidth);

  const resolvedDeviceType: 'mobile' | 'tablet' | 'desktop' = parserDeviceType ?? deviceTypeByWidth;

  return {
    isMobile: resolvedDeviceType === 'mobile',
    isTablet: resolvedDeviceType === 'tablet',
    isDesktop: resolvedDeviceType === 'desktop',
    os: operatingSystem,
    browser: webBrowser,
    browserVersion: webBrowserVersion,
    deviceType: resolvedDeviceType,
    isStandalonePWA: isStandalonePWA,
  };
}

export function computeUserAgentSnapshot(input: {
  userAgentString: string;
  windowInnerWidth: number;
  matchMedia?: (query: string) => MediaQueryList;
  standalone?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userAgentData?: any;
  userAgentParserResult?: ReturnType<UAParser['getResult']>;
}): UAInfo {
  const userAgentParserResult =
    input.userAgentParserResult ?? new UAParser(input.userAgentString).getResult();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clientHintsData: any = input.userAgentData;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clientHintsIndicatesMobile = !!clientHintsData?.mobile;

  const isDisplayModeStandalone =
    (input.matchMedia?.('(display-mode: standalone)').matches ?? false) ||
    input.standalone === true;

  return computeUserAgentInfo(
    userAgentParserResult,
    input.windowInnerWidth,
    isDisplayModeStandalone
  );
}

export function useUserAgent(): UAInfo {
  const [userAgentInformation, setUserAgentInformation] = useState<UAInfo>(() => ({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    os: '',
    browser: '',
    browserVersion: '',
    deviceType: 'desktop',
    isStandalonePWA: false,
  }));

  useEffect(() => {
    /* istanbul ignore next -- jsdom always defines window */
    if (typeof window === 'undefined') return;

    const updateUserAgentInformation = () => {
      const userAgentString = navigator.userAgent;
      setUserAgentInformation(
        computeUserAgentSnapshot({
          userAgentString,
          windowInnerWidth: window.innerWidth,
          matchMedia: window.matchMedia?.bind(window),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          standalone: (navigator as any).standalone === true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          userAgentData: (navigator as any).userAgentData,
        })
      );
    };

    updateUserAgentInformation();

    const onWindowResize = () => updateUserAgentInformation();
    const onDocumentVisibilityChange = () => updateUserAgentInformation();
    const onWindowOrientationChange = () => updateUserAgentInformation();

    window.addEventListener('resize', onWindowResize);
    window.addEventListener(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'orientationchange' as any,
      onWindowOrientationChange as unknown as EventListener
    );
    document.addEventListener('visibilitychange', onDocumentVisibilityChange);

    let displayModeMediaQueryList: MediaQueryList | undefined;
    try {
      displayModeMediaQueryList = window.matchMedia?.('(display-mode: standalone)');
      displayModeMediaQueryList?.addEventListener?.('change', updateUserAgentInformation);
    } catch (error) {
      console.log(error);
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).userAgentData?.addEventListener?.('change', updateUserAgentInformation);
    } catch (error) {
      console.log(error);
    }

    return () => {
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'orientationchange' as any,
        onWindowOrientationChange as unknown as EventListener
      );
      document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
      try {
        displayModeMediaQueryList?.removeEventListener?.('change', updateUserAgentInformation);
      } catch (error) {
        console.log(error);
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (navigator as any).userAgentData?.removeEventListener?.(
          'change',
          updateUserAgentInformation
        );
      } catch (error) {
        console.log(error);
      }
    };
  }, []);

  return useMemo(() => userAgentInformation, [userAgentInformation]);
}
